import React, { useState, useEffect } from 'react';
import { BuildingOfficeIcon, UserIcon, UsersIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/authService';
import { validateDocument, getValidationClass, getValidationMessage } from '../../utils/validators';

const ContractParties = ({ contractData, selectedClient, onChange }) => {
  // Estado para dados da empresa (LOCADOR) carregados do banco
  const [locadorData, setLocadorData] = useState({
    razao_social: '',
    cnpj: '',
    endereco: '',
    cidade: '',
    estado: '',
    cep: '',
    representante_nome: '',
    representante_cargo: '',
    representante_cpf: '',
    representante_rg: '',
    representante_email: ''
  });

  // Estado para rastrear campos CPF/CNPJ tocados (para validação visual)
  const [touchedFields, setTouchedFields] = useState({
    locador_representante_cpf: false,
    testemunha1_cpf: false,
    testemunha2_cpf: false
  });
  const [locatarioSociosTouched, setLocatarioSociosTouched] = useState({});

  const handleLocadorChange = (field, value) => {
    onChange({
      ...contractData,
      [`locador_${field}`]: value
    });
  };

  const handleLocatarioChange = (field, value) => {
    onChange({
      ...contractData,
      [`locatario_${field}`]: value
    });
  };

  const handleTestemunhaChange = (index, field, value) => {
    onChange({
      ...contractData,
      [`testemunha${index}_${field}`]: value
    });
  };

  // Carregar dados da empresa e sócio principal do banco de dados
  useEffect(() => {
    const loadCompanyData = async () => {
      try {
        console.log('🔄 Carregando dados da empresa do banco...');

        // Carregar configurações da empresa
        const settingsResponse = await api.get('/api/system-settings');
        const settings = settingsResponse.data.data || [];

        console.log(`📥 Recebidos ${settings.length} settings do banco`);

        // Converter array de settings em objeto
        const settingsObj = {};
        settings.forEach(setting => {
          settingsObj[setting.setting_key] = setting.setting_value;
        });

        console.log('🏢 Dados da empresa no banco:', {
          razao_social: settingsObj.company_name,
          cnpj: settingsObj.company_cnpj,
          endereco: settingsObj.company_address,
          cidade: settingsObj.company_city,
          estado: settingsObj.company_state,
          cep: settingsObj.company_cep
        });

        // Carregar sócio principal
        let primaryPartner = null;
        try {
          const partnerResponse = await api.get('/api/company-partners/primary');
          // API retorna { data: { ... } }
          if (partnerResponse.data && partnerResponse.data.data) {
            primaryPartner = partnerResponse.data.data;
            console.log('✅ Sócio principal carregado:', primaryPartner);
          }
        } catch (partnerError) {
          console.log('⚠️ Nenhum sócio principal configurado ainda');
        }

        // Atualizar locadorData com dados do banco
        const newLocadorData = {
          razao_social: settingsObj.company_name || 'VIPLOC LOCAÇÕES LTDA',
          cnpj: settingsObj.company_cnpj || '00.000.000/0001-00',
          endereco: settingsObj.company_address || 'Rua Exemplo, 123 - Centro',
          cidade: settingsObj.company_city || 'Campo Grande',
          estado: settingsObj.company_state || 'MS',
          cep: settingsObj.company_cep || '79000-000',
          // Dados do representante (sócio principal)
          representante_nome: primaryPartner?.name || 'Anderson Batista de Andrade',
          representante_cargo: primaryPartner?.position || 'Sócio Administrador',
          representante_cpf: primaryPartner?.cpf || '914.299.512-49',
          representante_rg: primaryPartner?.rg || '000000000',
          representante_email: primaryPartner?.email || ''
        };

        console.log('✅ Atualizando locadorData com:', newLocadorData);
        setLocadorData(newLocadorData);
      } catch (error) {
        console.error('❌ Erro ao carregar dados da empresa:', error);
      }
    };

    loadCompanyData();
  }, []);

  // Carregar dados padrão do locador se não existirem no contractData
  // IMPORTANTE: Preenche SEMPRE que qualquer campo crítico estiver vazio
  useEffect(() => {
    // Só preenche se os dados do banco já foram carregados (representante_nome não vazio)
    if (!locadorData.representante_nome) {
      console.log('⏳ Aguardando dados da empresa do banco...');
      return;
    }

    // Verificar se algum campo crítico do locador está vazio NO contractData
    const needsLocadorData = !contractData.locador_razao_social ||
                             !contractData.locador_representante_nome ||
                             !contractData.locador_representante_cpf;

    if (needsLocadorData) {
      console.log('🔧 Preenchendo dados do LOCADOR automaticamente...');
      console.log('📊 locadorData do banco:', locadorData);

      // Construir objeto com TODOS os campos do locador de uma vez
      const locadorFields = {};
      Object.keys(locadorData).forEach(key => {
        // Só preenche se o campo atual estiver vazio
        if (!contractData[`locador_${key}`]) {
          locadorFields[`locador_${key}`] = locadorData[key];
        }
      });

      console.log('📤 Campos a serem preenchidos:', locadorFields);

      // Atualizar TUDO de uma vez só (evita conflitos de múltiplas chamadas setState)
      if (Object.keys(locadorFields).length > 0) {
        onChange({
          ...contractData,
          ...locadorFields
        });
      }
    }
  }, [locadorData]);

  // Auto-popular dados do cliente quando selecionado
  useEffect(() => {
    if (selectedClient) {
      console.log('🔧 Cliente selecionado:', selectedClient);
      console.log('🔧 Partners do cliente:', selectedClient.partners);

      // Preencher dados básicos do locatário
      onChange({
        ...contractData,
        locatario_razao_social: selectedClient.name || '',
        locatario_cnpj: selectedClient.document || '',
        locatario_email: selectedClient.email || '',
        locatario_telefone: selectedClient.phone || '',
        locatario_endereco: typeof selectedClient.address === 'object'
          ? `${selectedClient.address.street || ''}${selectedClient.address.number ? ', ' + selectedClient.address.number : ''}${selectedClient.address.complement ? ' - ' + selectedClient.address.complement : ''} - ${selectedClient.address.neighborhood || ''}, ${selectedClient.address.city || ''} - ${selectedClient.address.state || ''}, CEP: ${selectedClient.address.cep || ''}`
          : selectedClient.address || '',
        // Carregar sócios do cliente (se houver)
        locatario_socios: selectedClient.partners && selectedClient.partners.length > 0
          ? selectedClient.partners
          : [{ name: '', document: '', participation: '', email: '' }]
      });
    }
  }, [selectedClient]);

  return (
    <div className="space-y-6">
      {/* LOCADOR */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 bg-blue-50 dark:bg-blue-900/10">
        <div className="flex items-center gap-3 mb-4">
          <BuildingOfficeIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              LOCADOR (Empresa)
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Quem está alugando o veículo
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Razão Social *
            </label>
            <input
              type="text"
              value={contractData.locador_razao_social || locadorData.razao_social}
              onChange={(e) => handleLocadorChange('razao_social', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              CNPJ *
            </label>
            <input
              type="text"
              value={contractData.locador_cnpj || locadorData.cnpj}
              onChange={(e) => handleLocadorChange('cnpj', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Cidade/Estado *
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={contractData.locador_cidade || locadorData.cidade}
                onChange={(e) => handleLocadorChange('cidade', e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Cidade"
              />
              <input
                type="text"
                value={contractData.locador_estado || locadorData.estado}
                onChange={(e) => handleLocadorChange('estado', e.target.value)}
                className="w-20 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="UF"
              />
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Representante Legal *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Nome Completo
                </label>
                <input
                  type="text"
                  value={contractData.locador_representante_nome || locadorData.representante_nome}
                  onChange={(e) => handleLocadorChange('representante_nome', e.target.value)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full"
                  placeholder="Nome completo"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={contractData.locador_representante_email || locadorData.representante_email}
                  onChange={(e) => handleLocadorChange('representante_email', e.target.value)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full"
                  placeholder="email@exemplo.com"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Cargo
                </label>
                <input
                  type="text"
                  value={contractData.locador_representante_cargo || locadorData.representante_cargo}
                  onChange={(e) => handleLocadorChange('representante_cargo', e.target.value)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full"
                  placeholder="Cargo"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  CPF
                </label>
                <input
                  type="text"
                  value={contractData.locador_representante_cpf || locadorData.representante_cpf}
                  onChange={(e) => handleLocadorChange('representante_cpf', e.target.value)}
                  onBlur={() => setTouchedFields(prev => ({...prev, locador_representante_cpf: true}))}
                  className={`px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full ${getValidationClass(contractData.locador_representante_cpf || locadorData.representante_cpf, touchedFields.locador_representante_cpf) || 'border-gray-300 dark:border-gray-600'}`}
                  placeholder="CPF"
                />
                {touchedFields.locador_representante_cpf && (contractData.locador_representante_cpf || locadorData.representante_cpf) && (
                  <p className={`text-xs mt-1 ${getValidationMessage(contractData.locador_representante_cpf || locadorData.representante_cpf, touchedFields.locador_representante_cpf).isValid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {getValidationMessage(contractData.locador_representante_cpf || locadorData.representante_cpf, touchedFields.locador_representante_cpf).message}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* LOCATÁRIO */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 bg-green-50 dark:bg-green-900/10">
        <div className="flex items-center gap-3 mb-4">
          <UserIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              LOCATÁRIO (Cliente)
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Quem está alugando o veículo
            </p>
          </div>
        </div>

        {selectedClient ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-green-200 dark:border-green-800">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Nome/Razão Social:</span>
                <p className="text-gray-900 dark:text-white">{selectedClient.name}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">CPF/CNPJ:</span>
                <p className="text-gray-900 dark:text-white">{selectedClient.document}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Email:</span>
                <p className="text-gray-900 dark:text-white">{selectedClient.email}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Telefone:</span>
                <p className="text-gray-900 dark:text-white">{selectedClient.phone}</p>
              </div>
              {selectedClient.address && (
                <div className="md:col-span-2">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Endereço:</span>
                  <p className="text-gray-900 dark:text-white">
                    {typeof selectedClient.address === 'object'
                      ? `${selectedClient.address.street || ''}${selectedClient.address.number ? ', ' + selectedClient.address.number : ''}${selectedClient.address.complement ? ' - ' + selectedClient.address.complement : ''} - ${selectedClient.address.neighborhood || ''}, ${selectedClient.address.city || ''} - ${selectedClient.address.state || ''}, CEP: ${selectedClient.address.cep || ''}`
                      : selectedClient.address}
                  </p>
                </div>
              )}
            </div>

            {/* Sócios do Cliente (se for PJ) */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Sócios da Empresa Cliente
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const currentPartners = contractData.locatario_socios || (selectedClient?.partners && selectedClient.partners.length > 0 ? selectedClient.partners : [{ name: '', document: '', participation: '' }]);
                    onChange({
                      ...contractData,
                      locatario_socios: [...currentPartners, { name: '', document: '', participation: '' }]
                    });
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <span className="text-lg font-bold">+</span>
                  <span>Adicionar Sócio</span>
                </button>
              </div>

              <div className="space-y-3">
                {(contractData.locatario_socios || (selectedClient?.partners && selectedClient.partners.length > 0 ? selectedClient.partners : [{ name: '', document: '', participation: '' }])).map((socio, index) => (
                  <div key={index} className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Sócio {index + 1}
                      </span>
                      {(contractData.locatario_socios || selectedClient?.partners || [{ name: '', document: '', participation: '' }]).length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const currentPartners = contractData.locatario_socios || selectedClient?.partners || [{ name: '', document: '', participation: '' }];
                            const updatedPartners = currentPartners.filter((_, i) => i !== index);
                            onChange({
                              ...contractData,
                              locatario_socios: updatedPartners
                            });
                          }}
                          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium"
                        >
                          Remover
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <input
                        type="text"
                        value={socio.name || ''}
                        onChange={(e) => {
                          const currentPartners = contractData.locatario_socios || selectedClient?.partners || [{ name: '', document: '', participation: '' }];
                          const updatedPartners = [...currentPartners];
                          updatedPartners[index] = { ...updatedPartners[index], name: e.target.value };
                          onChange({
                            ...contractData,
                            locatario_socios: updatedPartners
                          });
                        }}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Nome completo"
                      />
                      <input
                        type="email"
                        value={socio.email || ''}
                        onChange={(e) => {
                          const currentPartners = contractData.locatario_socios || selectedClient?.partners || [{ name: '', document: '', participation: '', email: '' }];
                          const updatedPartners = [...currentPartners];
                          updatedPartners[index] = { ...updatedPartners[index], email: e.target.value };
                          onChange({
                            ...contractData,
                            locatario_socios: updatedPartners
                          });
                        }}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="email@exemplo.com"
                      />
                      <div>
                        <input
                          type="text"
                          value={socio.document || ''}
                          onChange={(e) => {
                            const currentPartners = contractData.locatario_socios || selectedClient?.partners || [{ name: '', document: '', participation: '' }];
                            const updatedPartners = [...currentPartners];
                            updatedPartners[index] = { ...updatedPartners[index], document: e.target.value };
                            onChange({
                              ...contractData,
                              locatario_socios: updatedPartners
                            });
                          }}
                          onBlur={() => setLocatarioSociosTouched(prev => ({...prev, [index]: true}))}
                          className={`px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full ${getValidationClass(socio.document, locatarioSociosTouched[index]) || 'border-gray-300 dark:border-gray-600'}`}
                          placeholder="CPF"
                        />
                        {locatarioSociosTouched[index] && socio.document && (
                          <p className={`text-xs mt-1 ${getValidationMessage(socio.document, locatarioSociosTouched[index]).isValid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {getValidationMessage(socio.document, locatarioSociosTouched[index]).message}
                          </p>
                        )}
                      </div>
                      <input
                        type="number"
                        value={socio.participation || ''}
                        onChange={(e) => {
                          const currentPartners = contractData.locatario_socios || selectedClient?.partners || [{ name: '', document: '', participation: '' }];
                          const updatedPartners = [...currentPartners];
                          updatedPartners[index] = { ...updatedPartners[index], participation: e.target.value };
                          onChange({
                            ...contractData,
                            locatario_socios: updatedPartners
                          });
                        }}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Participação %"
                        min="0"
                        max="100"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quem assina pelo cliente? */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Quem assina pelo cliente?
              </label>
              <div className="flex gap-3 mb-4">
                {[
                  { value: 'socio', label: 'Sócio(s)' },
                  { value: 'procurador', label: 'Procurador' },
                  { value: 'outro', label: 'Outro (manual)' }
                ].map(opt => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="signatario_tipo"
                      value={opt.value}
                      checked={(contractData.signatario_tipo || 'socio') === opt.value}
                      onChange={() => {
                        const tipo = opt.value;
                        let updates = { signatario_tipo: tipo };
                        if (tipo === 'procurador') {
                          const lr = selectedClient?.legal_representative;
                          updates.locatario_representante_nome = lr?.nome || '';
                          updates.locatario_representante_cpf = lr?.cpf || '';
                          updates.locatario_representante_rg = lr?.rg || '';
                          updates.locatario_representante_cargo = 'Procurador(a)';
                          updates.locatario_representante_instrumento = lr?.instrumento || 'Instrumento Particular de Procuração';
                        }
                        onChange({ ...contractData, ...updates });
                      }}
                      className="text-blue-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{opt.label}</span>
                  </label>
                ))}
              </div>

              {/* Sócios — checkboxes para selecionar quem assina */}
              {(contractData.signatario_tipo || 'socio') === 'socio' && (() => {
                const socios = contractData.locatario_socios || selectedClient?.partners || [];
                if (socios.length === 0) return (
                  <div className="mb-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg text-sm text-yellow-800 dark:text-yellow-200">
                    Nenhum sócio cadastrado. Adicione sócios acima ou mude para "Outro (manual)".
                  </div>
                );
                return (
                  <div className="mb-3 space-y-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Selecione quais sócios irão assinar o contrato:</p>
                    {socios.map((socio, index) => {
                      const assina = socio.assina !== false; // default true
                      return (
                        <label key={index} className="flex items-center gap-3 p-2 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                          <input
                            type="checkbox"
                            checked={assina}
                            onChange={e => {
                              const updated = socios.map((s, i) =>
                                i === index ? { ...s, assina: e.target.checked } : s
                              );
                              onChange({ ...contractData, locatario_socios: updated });
                            }}
                            className="text-blue-600 w-4 h-4"
                          />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{socio.name || socio.nome || `Sócio ${index + 1}`}</span>
                            {(socio.document || socio.cpf) && <span className="text-xs text-gray-400 ml-2">CPF: {socio.document || socio.cpf}</span>}
                            {socio.email && <span className="text-xs text-gray-400 ml-2">· {socio.email}</span>}
                          </div>
                          {assina && <span className="text-xs text-green-600 font-medium">✓ Assina</span>}
                        </label>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Alerta se Procurador sem dados cadastrados */}
              {(contractData.signatario_tipo || 'socio') === 'procurador' && !selectedClient?.legal_representative && (
                <div className="mb-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg text-sm text-yellow-800 dark:text-yellow-200">
                  Cliente sem procurador cadastrado. Edite o cadastro do cliente e preencha a aba "Procurador", ou preencha os campos abaixo manualmente.
                </div>
              )}

              {/* Campos do representante — só para procurador ou outro */}
              {(contractData.signatario_tipo === 'procurador' || contractData.signatario_tipo === 'outro') && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Nome do Representante *</label>
                  <input
                    type="text"
                    value={contractData.locatario_representante_nome || ''}
                    onChange={e => onChange({ ...contractData, locatario_representante_nome: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    placeholder="Nome completo"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Cargo / Qualificação *</label>
                  <input
                    type="text"
                    value={contractData.locatario_representante_cargo || ''}
                    onChange={e => onChange({ ...contractData, locatario_representante_cargo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    placeholder="Ex: sócio administrador"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">CPF</label>
                  <input
                    type="text"
                    value={contractData.locatario_representante_cpf || ''}
                    onChange={e => onChange({ ...contractData, locatario_representante_cpf: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    placeholder="000.000.000-00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">RG</label>
                  <input
                    type="text"
                    value={contractData.locatario_representante_rg || ''}
                    onChange={e => onChange({ ...contractData, locatario_representante_rg: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    placeholder="00.000.000-0"
                  />
                </div>
              </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Selecione um cliente acima para preencher automaticamente
          </div>
        )}
      </div>

      {/* TESTEMUNHAS */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 bg-purple-50 dark:bg-purple-900/10">
        <div className="flex items-center gap-3 mb-4">
          <UsersIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              TESTEMUNHAS
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              2 testemunhas obrigatórias
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Testemunha 1 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Testemunha 1 *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input
                type="text"
                value={contractData.testemunha1_nome || ''}
                onChange={(e) => handleTestemunhaChange(1, 'nome', e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full self-start"
                placeholder="Nome completo"
                required
              />
              <div>
                <input
                  type="email"
                  value={contractData.testemunha1_email || ''}
                  onChange={(e) => handleTestemunhaChange(1, 'email', e.target.value)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full"
                  placeholder="email@exemplo.com"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Para receber contrato no Clicksign
                </p>
              </div>
              <div>
                <input
                  type="text"
                  value={contractData.testemunha1_cpf || ''}
                  onChange={(e) => handleTestemunhaChange(1, 'cpf', e.target.value)}
                  onBlur={() => setTouchedFields(prev => ({...prev, testemunha1_cpf: true}))}
                  className={`px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full ${getValidationClass(contractData.testemunha1_cpf, touchedFields.testemunha1_cpf) || 'border-gray-300 dark:border-gray-600'}`}
                  placeholder="CPF"
                  required
                />
                {touchedFields.testemunha1_cpf && contractData.testemunha1_cpf && (
                  <p className={`text-xs mt-1 ${getValidationMessage(contractData.testemunha1_cpf, touchedFields.testemunha1_cpf).isValid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {getValidationMessage(contractData.testemunha1_cpf, touchedFields.testemunha1_cpf).message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Testemunha 2 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Testemunha 2 *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input
                type="text"
                value={contractData.testemunha2_nome || ''}
                onChange={(e) => handleTestemunhaChange(2, 'nome', e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full self-start"
                placeholder="Nome completo"
                required
              />
              <div>
                <input
                  type="email"
                  value={contractData.testemunha2_email || ''}
                  onChange={(e) => handleTestemunhaChange(2, 'email', e.target.value)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full"
                  placeholder="email@exemplo.com"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Para receber contrato no Clicksign
                </p>
              </div>
              <div>
                <input
                  type="text"
                  value={contractData.testemunha2_cpf || ''}
                  onChange={(e) => handleTestemunhaChange(2, 'cpf', e.target.value)}
                  onBlur={() => setTouchedFields(prev => ({...prev, testemunha2_cpf: true}))}
                  className={`px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full ${getValidationClass(contractData.testemunha2_cpf, touchedFields.testemunha2_cpf) || 'border-gray-300 dark:border-gray-600'}`}
                  placeholder="CPF"
                  required
                />
                {touchedFields.testemunha2_cpf && contractData.testemunha2_cpf && (
                  <p className={`text-xs mt-1 ${getValidationMessage(contractData.testemunha2_cpf, touchedFields.testemunha2_cpf).isValid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {getValidationMessage(contractData.testemunha2_cpf, touchedFields.testemunha2_cpf).message}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractParties;
